import React from 'react';
import { Link } from 'react-router-dom';

const BOOKING_URL = 'https://cryobybreum.planway.com/';

export default function EmsSeoSection() {
  return (
    <div className="ems-seo-section">
      {/* 1. INTRODUKTION & HOVEDBUDSKAB */}
      <section className="py-20 lg:py-28 bg-cream border-b border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6 reveal-on-scroll">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-sand text-espresso text-[11px] font-semibold uppercase tracking-widest border border-taupe-border">
              <i className="fa-solid fa-bolt-lightning text-accent-brown text-xs" aria-hidden="true" />
              <span>Elektrisk muskelstimulation · Odense SV</span>
            </div>

            <h2 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso uppercase tracking-wider leading-[1.2]">
              EMS træning i Odense, få mere ud af din muskelaktivering
            </h2>

            <div className="pl-4 sm:pl-6 border-l-2 border-accent-brown/70 py-1">
              <p className="text-base sm:text-lg text-taupe font-normal leading-relaxed">
                Hos{' '}
                <Link to="/" className="text-espresso font-semibold hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/50 hover:decoration-accent-brown transition-colors">
                  Cryo By Breum
                </Link>{' '}
                kan du prøve EMS træning i Odense, hvor kontrollerede impulser hjælper med at aktivere musklerne under en fokuseret session. Vi bruger elektrisk muskelstimulation med blik for dit niveau, din oplevelse og dine mål, så du får en rolig introduktion og ved, hvad der sker undervejs.
              </p>
            </div>

            {/* Hurtige overblikspunkter */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6">
              <div className="p-5 bg-sand-light/60 border border-taupe-border/80">
                <span className="block text-[10px] font-bold text-accent-brown uppercase tracking-widest">Session</span>
                <span className="text-sm font-semibold text-espresso block mt-1">20–25 minutter</span>
                <span className="text-xs text-taupe mt-0.5 block">Kompakt & fokuseret form</span>
              </div>
              <div className="p-5 bg-sand-light/60 border border-taupe-border/80">
                <span className="block text-[10px] font-bold text-accent-brown uppercase tracking-widest">Vejledning</span>
                <span className="text-sm font-semibold text-espresso block mt-1">1:1 personlig instruktør</span>
                <span className="text-xs text-taupe mt-0.5 block">Løbende afstemning</span>
              </div>
              <div className="p-5 bg-sand-light/60 border border-taupe-border/80">
                <span className="block text-[10px] font-bold text-accent-brown uppercase tracking-widest">Lokation</span>
                <span className="text-sm font-semibold text-espresso block mt-1">Dalumvej 82, Odense</span>
                <span className="text-xs text-taupe mt-0.5 block">Privat og roligt studie</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. HVAD ER EMS TRÆNING, OG HVORFOR FØLES DEN ANDERLEDES? */}
      <section className="py-20 lg:py-24 bg-warm-beige border-b border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12 reveal-on-scroll">
            <div className="max-w-3xl space-y-3">
              <span className="text-[11px] font-bold uppercase tracking-widest text-accent-brown">Metode & Oplevelse</span>
              <h3 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                Hvad er EMS træning, og hvorfor føles den anderledes?
              </h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
              {/* Kort 1: Helt konkret */}
              <div className="p-8 sm:p-10 bg-cream border border-taupe-border/80 space-y-5 flex flex-col justify-between shadow-xs">
                <div className="space-y-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-sand border border-taupe-border text-espresso">
                    <i className="fa-solid fa-wave-pulse text-lg" aria-hidden="true" />
                  </div>
                  <h4 className="font-sans text-lg sm:text-xl font-semibold text-espresso uppercase tracking-wider">
                    Hvad er EMS træning helt konkret?
                  </h4>
                  <p className="text-sm sm:text-base text-taupe leading-relaxed">
                    EMS står for elektrisk muskelstimulation. Via elektroder sendes kontrollerede impulser til udvalgte muskler, så de trækker sig sammen. Impulserne arbejder sammen med din egen muskelaktivering og kan gøre selv enkle bevægelser mere mærkbare.
                  </p>
                </div>
                <div className="pt-4 border-t border-taupe-border/50 text-xs text-taupe-light flex items-center gap-2">
                  <i className="fa-solid fa-check text-accent-brown" aria-hidden="true" />
                  <span>Aktiverer dybereliggende muskellag samtidigt</span>
                </div>
              </div>

              {/* Kort 2: Fornemmelsen */}
              <div className="p-8 sm:p-10 bg-cream border border-taupe-border/80 space-y-5 flex flex-col justify-between shadow-xs">
                <div className="space-y-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-sand border border-taupe-border text-espresso">
                    <i className="fa-solid fa-heart-pulse text-lg" aria-hidden="true" />
                  </div>
                  <h4 className="font-sans text-lg sm:text-xl font-semibold text-espresso uppercase tracking-wider">
                    En mærkbar og gradvis opbygning
                  </h4>
                  <p className="text-sm sm:text-base text-taupe leading-relaxed">
                    For de fleste føles impulserne som tydelige og rytmiske spændinger i musklerne. Intensiteten bygges gradvist op og skal passe til dig. Det må gerne kunne mærkes, men det skal ikke gøre ondt. Derfor er dialog vigtig, både ved første besøg og når din krop har vænnet sig til formen.
                  </p>
                </div>
                <div className="pt-4 border-t border-taupe-border/50 text-xs text-taupe-light flex items-center gap-2">
                  <i className="fa-solid fa-comments text-accent-brown" aria-hidden="true" />
                  <span>Kontinuerlig dialog med din personlige instruktør</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SÅDAN SKABER VI EN TRYG START */}
      <section className="py-20 lg:py-28 bg-sand-light border-b border-taupe-border/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12 reveal-on-scroll">
            <div className="max-w-3xl space-y-4">
              <span className="text-[11px] font-bold uppercase tracking-widest text-accent-brown">Trin For Trin</span>
              <h3 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                Sådan skaber vi en tryg start
              </h3>
              <p className="text-base sm:text-lg text-taupe leading-relaxed">
                Vi begynder med en kort samtale om din erfaring, dit aktivitetsniveau og det, du gerne vil have ud af EMS træning. Du får forklaret forløbet, og vi afstemmer intensiteten løbende. På den måde bliver elektrisk muskelstimulation en overskuelig oplevelse frem for noget, du blot skal igennem.
              </p>
            </div>

            {/* 4 Trin i struktureret opstilling */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Trin 1 */}
              <div className="p-6 bg-cream border border-taupe-border relative flex flex-col justify-between space-y-6 hover:border-espresso/50 transition-colors">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-sans text-2xl font-bold text-accent-brown tracking-tighter">01</span>
                    <i className="fa-solid fa-clipboard-check text-taupe text-sm" aria-hidden="true" />
                  </div>
                  <h4 className="font-sans text-xs font-bold text-espresso uppercase tracking-wider">
                    Mål & hensyn
                  </h4>
                  <p className="text-sm text-taupe leading-relaxed">
                    En afklaring af dine mål og relevante hensyn før du går i gang
                  </p>
                </div>
                <div className="h-0.5 w-10 bg-accent-brown/30" />
              </div>

              {/* Trin 2 */}
              <div className="p-6 bg-cream border border-taupe-border relative flex flex-col justify-between space-y-6 hover:border-espresso/50 transition-colors">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-sans text-2xl font-bold text-accent-brown tracking-tighter">02</span>
                    <i className="fa-solid fa-vest text-taupe text-sm" aria-hidden="true" />
                  </div>
                  <h4 className="font-sans text-xs font-bold text-espresso uppercase tracking-wider">
                    Udstyr & følelse
                  </h4>
                  <p className="text-sm text-taupe leading-relaxed">
                    En rolig introduktion til udstyret og følelsen i musklerne
                  </p>
                </div>
                <div className="h-0.5 w-10 bg-accent-brown/30" />
              </div>

              {/* Trin 3 */}
              <div className="p-6 bg-cream border border-taupe-border relative flex flex-col justify-between space-y-6 hover:border-espresso/50 transition-colors">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-sans text-2xl font-bold text-accent-brown tracking-tighter">03</span>
                    <i className="fa-solid fa-sliders text-taupe text-sm" aria-hidden="true" />
                  </div>
                  <h4 className="font-sans text-xs font-bold text-espresso uppercase tracking-wider">
                    Løbende tilpasning
                  </h4>
                  <p className="text-sm text-taupe leading-relaxed">
                    Løbende tilpasning, så intensiteten opleves tydelig og håndterbar
                  </p>
                </div>
                <div className="h-0.5 w-10 bg-accent-brown/30" />
              </div>

              {/* Trin 4 */}
              <div className="p-6 bg-cream border border-taupe-border relative flex flex-col justify-between space-y-6 hover:border-espresso/50 transition-colors">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-sans text-2xl font-bold text-accent-brown tracking-tighter">04</span>
                    <i className="fa-solid fa-circle-question text-taupe text-sm" aria-hidden="true" />
                  </div>
                  <h4 className="font-sans text-xs font-bold text-espresso uppercase tracking-wider">
                    Afrunding & dialog
                  </h4>
                  <p className="text-sm text-taupe leading-relaxed">
                    En afrunding med plads til spørgsmål om det næste skridt
                  </p>
                </div>
                <div className="h-0.5 w-10 bg-accent-brown/30" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. VIRKER EMS TRÆNING, OG HVAD KAN DU FORVENTE? + RESULTATER SKABES AF MERE END IMPULSER */}
      <section className="py-20 lg:py-28 bg-cream border-b border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start reveal-on-scroll">
            {/* Venstre kolonne: Virker EMS & realistiske forventninger */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-[11px] font-bold uppercase tracking-widest text-accent-brown">Forventningsafstemning</span>
              <h3 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                Virker EMS træning, og hvad kan du forvente?
              </h3>
              
              <div className="space-y-4 text-sm sm:text-base text-taupe leading-relaxed">
                <p>
                  Mange stiller spørgsmålet EMS træning virker det? Det ærlige svar er, at elektrisk muskelstimulation kan aktivere musklerne, men at udbyttet varierer. Din indsats, søvn, kost og øvrige aktivitet har også betydning. EMS er derfor ikke en genvej, der kan stå alene, og vi lover ikke bestemte resultater.
                </p>
                <p>
                  EMS træning giver bedst mening som et målrettet supplement til en aktiv hverdag. Nogle ønsker bedre kontakt til bestemte muskelgrupper, mens andre søger en kompakt træningsform, der er nemmere at få passet ind. Vi hjælper dig med realistiske forventninger og en løsning, der tager udgangspunkt i dig.
                </p>
              </div>

              {/* Hvem kan have glæde af det */}
              <div className="pt-6 border-t border-taupe-border/60 space-y-3">
                <h4 className="font-sans text-base sm:text-lg font-semibold text-espresso uppercase tracking-wider">
                  Hvem kan have glæde af elektrisk muskelstimulation?
                </h4>
                <p className="text-sm sm:text-base text-taupe leading-relaxed">
                  Elektrisk muskelstimulation kan være relevant, hvis du er nysgerrig på en anderledes måde at aktivere musklerne på. Det kan også være en mulighed, hvis du ønsker en fokuseret session med vejledning. Formen skal vælges ud fra dit helbred, dit niveau og det, du trives med.
                </p>
              </div>
            </div>

            {/* Højre kolonne: Resultater skabes af mere end impulser */}
            <div className="lg:col-span-5 space-y-6">
              <div className="p-8 sm:p-10 bg-sand border border-taupe-border/90 space-y-6">
                <div className="inline-flex items-center gap-2 text-accent-brown text-xs font-bold uppercase tracking-wider">
                  <i className="fa-solid fa-chart-line" aria-hidden="true" />
                  <span>Kontinuitet & Udvikling</span>
                </div>
                <h4 className="font-sans text-xl sm:text-2xl font-semibold text-espresso uppercase tracking-wider leading-snug">
                  Resultater skabes af mere end impulser
                </h4>
                <p className="text-sm sm:text-base text-taupe leading-relaxed">
                  Dit udbytte påvirkes af, hvor du starter, hvor regelmæssigt du træner, og hvordan resten af din hverdag ser ud. Forandring kræver som regel kontinuitet. Vores mål er ikke at sælge hurtige løfter, men at skabe et forløb, hvor du kan følge din udvikling og løbende vurdere, om EMS passer til dig.
                </p>

                <div className="pt-4 border-t border-taupe-border/80 flex flex-col gap-3">
                  <div className="flex items-start gap-3 text-xs text-espresso">
                    <i className="fa-solid fa-circle-check text-accent-brown mt-0.5" aria-hidden="true" />
                    <span>Realistisk forventningsafstemning ved hver træning</span>
                  </div>
                  <div className="flex items-start gap-3 text-xs text-espresso">
                    <i className="fa-solid fa-circle-check text-accent-brown mt-0.5" aria-hidden="true" />
                    <span>Personlig sparring og justering af modstand</span>
                  </div>
                  <div className="flex items-start gap-3 text-xs text-espresso">
                    <i className="fa-solid fa-circle-check text-accent-brown mt-0.5" aria-hidden="true" />
                    <span>Fokus på langsigtede og holdbare vaner</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SIKKERHED KOMMER ALTID FØR INTENSITET (KONTRAINDIKATIONER) */}
      <section className="py-20 lg:py-24 bg-warm-beige border-b border-taupe-border/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8 reveal-on-scroll">
            {/* Header */}
            <div className="text-center space-y-3">
              <div className="inline-flex items-center justify-center gap-2 px-3 py-1 bg-sand border border-taupe-border text-accent-brown text-[11px] font-bold uppercase tracking-widest">
                <i className="fa-solid fa-shield-halved text-xs" aria-hidden="true" />
                <span>Sundhed & Forholdsregler</span>
              </div>
              <h3 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                Sikkerhed kommer altid før intensitet
              </h3>
              <p className="text-sm sm:text-base text-taupe max-w-2xl mx-auto leading-relaxed">
                EMS træning er ikke det rigtige valg for alle. Fortæl os altid om sygdom, skader, medicinsk udstyr og igangværende behandling, før du starter. Er der tvivl om, hvorvidt elektrisk muskelstimulation er forsvarlig for dig, bør du få en vurdering fra din læge først.
              </p>
            </div>

            {/* Kontraindikations-punkter */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
              <div className="p-6 bg-cream border border-taupe-border flex items-start gap-4">
                <div className="w-8 h-8 rounded-none shrink-0 bg-sand flex items-center justify-center text-accent-brown">
                  <i className="fa-solid fa-ban text-xs" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-sans text-xs font-bold text-espresso uppercase tracking-wider">
                    Graviditet & Implantater
                  </h4>
                  <p className="text-xs sm:text-sm text-taupe leading-relaxed">
                    Du er gravid eller har pacemaker eller andet elektronisk implantat
                  </p>
                </div>
              </div>

              <div className="p-6 bg-cream border border-taupe-border flex items-start gap-4">
                <div className="w-8 h-8 rounded-none shrink-0 bg-sand flex items-center justify-center text-accent-brown">
                  <i className="fa-solid fa-ban text-xs" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-sans text-xs font-bold text-espresso uppercase tracking-wider">
                    Sygdom & Operationer
                  </h4>
                  <p className="text-xs sm:text-sm text-taupe leading-relaxed">
                    Du har epilepsi, akut sygdom eller er blevet opereret for nylig
                  </p>
                </div>
              </div>

              <div className="p-6 bg-cream border border-taupe-border flex items-start gap-4">
                <div className="w-8 h-8 rounded-none shrink-0 bg-sand flex items-center justify-center text-accent-brown">
                  <i className="fa-solid fa-ban text-xs" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-sans text-xs font-bold text-espresso uppercase tracking-wider">
                    Smerter & Skader
                  </h4>
                  <p className="text-xs sm:text-sm text-taupe leading-relaxed">
                    Du har smerter, skade eller hudirritation i det område, der skal arbejdes med
                  </p>
                </div>
              </div>
            </div>

            {/* Disclaimer / Lægelig afklaring */}
            <div className="p-6 bg-sand-light border-l-4 border-accent-brown text-taupe text-xs sm:text-sm leading-relaxed">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-info text-accent-brown mt-0.5 shrink-0" aria-hidden="true" />
                <p>
                  Listen er ikke udtømmende, og en indledende afklaring er ikke det samme som lægelig rådgivning. Hvis du er usikker, hjælper vi gerne med at fortælle, hvilke oplysninger vi har brug for, men det er din læge, der skal vurdere spørgsmål om sygdom og behandling.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. EMS TRÆNING I ODENSE MED PLADS TIL DINE MÅL & SAMMENLIGNING */}
      <section className="py-20 lg:py-28 bg-sand border-b border-taupe-border/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center reveal-on-scroll">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-[11px] font-bold uppercase tracking-widest text-accent-brown">Lokal Træning i Odense SV</span>
              <h3 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                EMS træning i Odense med plads til dine mål
              </h3>
              
              <div className="space-y-4 text-sm sm:text-base text-taupe leading-relaxed">
                <p>
                  Vores EMS træning i Odense er for dig, der ønsker en nærværende oplevelse i lokale rammer. Vi tager os tid til at forklare metoden og møde dig i øjenhøjde, uanset om du kommer fra Odense eller et andet sted på Fyn. Du behøver ikke kende teknikken på forhånd.
                </p>
                <p>
                  Vil du sammenligne mulighederne, kan du se{' '}
                  <Link to="/priser" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    vores priser og forløb
                  </Link>{' '}
                  eller læse mere om vores øvrige kropsbehandlinger i Odense som{' '}
                  <Link to="/behandlinger/fedtfrysning" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    fedtfrysning
                  </Link>{' '}
                  og{' '}
                  <Link to="/behandlinger/body-roll" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    Body Roll
                  </Link>
                  . Her får du et samlet overblik og kan vælge ud fra dine ønsker.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/behandlinger/fedtfrysning"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-cream border border-espresso text-espresso text-xs font-semibold uppercase tracking-wider hover:bg-white transition-colors"
                >
                  <span>Læs om Fedtfrysning</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
                <Link
                  to="/behandlinger/body-roll"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-cream border border-espresso text-espresso text-xs font-semibold uppercase tracking-wider hover:bg-white transition-colors"
                >
                  <span>Læs om Body Roll</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Lokale rammer faktaboks */}
            <div className="lg:col-span-5">
              <div className="p-8 sm:p-10 bg-cream border border-taupe-border space-y-6">
                <h4 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider">
                  Træn hos Cryo by Breum
                </h4>
                <div className="space-y-4 text-xs sm:text-sm text-taupe">
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-location-dot text-accent-brown mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <strong className="text-espresso block font-medium">Adresse</strong>
                      <span>Dalumvej 82, 5250 Odense SV</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-clock text-accent-brown mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <strong className="text-espresso block font-medium">Sessioner</strong>
                      <span>Kompakte 25-minutters forløb med fleksibel booking</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-user-shield text-accent-brown mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <strong className="text-espresso block font-medium">Trygge rammer</strong>
                      <span>Individuel instruktion i uforstyrrede omgivelser</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. BOOK EN TID, NÅR DU ER KLAR TIL AT PRØVE */}
      <section className="py-20 lg:py-24 bg-cream border-b border-taupe-border/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 reveal-on-scroll">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-sand border border-taupe-border text-accent-brown text-[11px] font-bold uppercase tracking-widest">
            <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
            <span>Klar til at opleve EMS?</span>
          </div>

          <h3 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
            Book en tid, når du er klar til at prøve
          </h3>

          <p className="text-base sm:text-lg text-taupe max-w-2xl mx-auto leading-relaxed">
            Er du klar til at opleve, hvordan EMS føles i praksis, kan du booke tid hos Cryo By Breum. Vi glæder os til at tage imod dig og give dig en tryg introduktion til EMS træning i Odense. Har du spørgsmål inden dit besøg, er du velkommen til at kontakte os.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-brown text-white text-xs uppercase tracking-widest font-semibold hover:bg-accent-brown-hover transition-colors shadow-sm"
            >
              <span>Book tid hos Cryo by Breum</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
            </a>

            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-espresso text-xs uppercase tracking-widest font-semibold border border-espresso hover:bg-sand-light transition-colors"
            >
              <span>Kontakt os ved spørgsmål</span>
              <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
            </Link>

            <a
              href="#priser"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-taupe text-xs uppercase tracking-widest font-semibold hover:text-espresso transition-colors"
            >
              <span>Se priser nedenfor</span>
              <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
